/**
 * Autocomplete — shared types, Zod prop schema, and styling constants.
 *
 * A suggestion-driven text field: the member types, a server-backed list of
 * suggestions appears beneath the field, and picking one commits the value.
 * One component for every suggest-and-pick interaction (data source is a
 * prop, never a variant — ADR-020 Rules 4/6); platform implementations live
 * in Autocomplete.native.tsx and Autocomplete.web.tsx. Callbacks are
 * platform-specific and not part of the catalog-safe schema.
 *
 * Source of truth: Pangea Galapagos UI Design System — Beta
 * https://www.figma.com/design/Orc6uUodQaoXep59CGaUNw
 */

import { z } from 'zod';
import { colors } from '../../elements/colors';
import { typography } from '../../elements/typography';
import { spacing } from '../../elements/spacing';
import { radius } from '../../elements/radius';
import { states } from '../../elements/states';

/** One pickable suggestion. `id` is the opaque value the pick commits. */
export const autocompleteSuggestionSchema = z.object({
  id: z.string(),
  label: z.string(),
  /** Optional secondary line (e.g. a structured-address detail). */
  sublabel: z.string().optional(),
});
export type AutocompleteSuggestion = z.infer<typeof autocompleteSuggestionSchema>;

export const autocompletePropsSchema = z.object({
  label: z.string(),
  value: z.string().optional(),
  placeholder: z.string().optional(),
  helperText: z.string().optional(),
  errorText: z.string().optional(),
  /** Placeholder-only field: suppress the floating in-field label. */
  hideLabel: z.boolean().default(false),
  disabled: z.boolean().default(false),
  loading: z.boolean().default(false),
  suggestions: z.array(autocompleteSuggestionSchema).default([]),
  /**
   * Accessible name for the suggestion panel, announced when it appears.
   * Consumer-localized (the design system bakes no copy); defaults to the
   * field label when omitted.
   */
  suggestionsLabel: z.string().optional(),
  testID: z.string().optional(),
  accessibilityLabel: z.string().optional(),
});
export type AutocompleteProps = z.infer<typeof autocompletePropsSchema>;

/**
 * Hard render cap on the suggestion panel. Server contracts cap suggestion
 * payloads already (10 companies / 5 addresses); this local bound keeps the
 * unvirtualized panel safe by construction for any future consumer or payload.
 */
export const MAX_RENDERED_SUGGESTIONS = 10;

/** Border radius for the suggestion panel. */
export const SUGGESTION_PANEL_RADIUS = radius.xl;

/** Border for the suggestion panel and row dividers. */
export const SUGGESTION_PANEL_BORDER_WIDTH = states.borderWidth.default;
export const SUGGESTION_PANEL_BORDER_COLOR = colors.stroke.divider;
export const SUGGESTION_PANEL_BACKGROUND = colors.background.container;

/** Gap between the field and the suggestion panel. */
export const FIELD_TO_PANEL_GAP = spacing.s;

/** Padding inside a suggestion row (44pt+ touch target per ADR-025). */
export const SUGGESTION_ROW_VERTICAL_PADDING = spacing.m;
export const SUGGESTION_ROW_HORIZONTAL_PADDING = spacing.lg;

/** Gap between a suggestion label and its sublabel. */
export const SUGGESTION_TEXT_GAP = spacing.xs;

/** Typography for suggestion rows. */
export const SUGGESTION_LABEL_TYPOGRAPHY = typography.b1Regular;
export const SUGGESTION_SUBLABEL_TYPOGRAPHY = typography.b3Regular;
export const SUGGESTION_LABEL_COLOR = colors.text.primary;
export const SUGGESTION_SUBLABEL_COLOR = colors.text.secondary;
